import React, { useCallback, useState } from 'react';
import styles from './Actions.module.scss';
import { isHomeEDApp } from '@constants';
import Button from '@components/button/Button';
import { useNextContent } from '../../useNextContent';
import PauseModal from '../../pause-modal/PauseModal';
import { useAnalytics } from '@hooks/useAnalytics';
import { Events } from '@services/Events';
import { useUpdateLastVisitedLessonMutation } from '@api/graphql-codegen';
import './actions.css';
import { useUser } from '@src/hooks';
import { logger } from '../../../../utils/logger';

type Props = {
  children: React.ReactNode;
  fullScreen: boolean;
  leftButton?: React.ReactNode;
  rightButton?: React.ReactNode;
  totalPages?: number;
  pageNumber?: number;
  className?: string;
  hideNextButton?: boolean;
};

function Actions(props: Props) {
  const {children, fullScreen, rightButton, leftButton, totalPages, pageNumber, className = '', hideNextButton} = props;
  const {hasNextContent, selectNext, selectPrev, hasPrevContent, contentNext, isParentApp} = useNextContent();
  const [user] = useUser();
  const {trackEvent} = useAnalytics();

  const [pauseModal, setPauseModal] = useState(false);

  const [updateLastVisitedLesson] = useUpdateLastVisitedLessonMutation({
    onError(e) {
      logger.debug(`[e]`, e);
    }
  });

  const handleClosePauseModal = useCallback(() => {
    setPauseModal(false);
  }, []);

  const handlePause = useCallback(() => {
    if (!isHomeEDApp && contentNext && !isParentApp && contentNext.unitId) {
      updateLastVisitedLesson({
        variables: {
          input: {
            ...contentNext
          }
        }
      });
    }
    handleClosePauseModal();
  }, [handleClosePauseModal, updateLastVisitedLesson, contentNext, isParentApp])

  const handleContinue = useCallback(() => {
    selectNext();
    handleClosePauseModal();
  }, [handleClosePauseModal, selectNext]);

  const onNext = useCallback(() => {
    selectNext();
    trackEvent(Events.actions.contentContinueButtonPressed, {
      pageNumber,
      content: contentNext?.moduleId,
      schoolId: user?.schools?.[0]?.id,
      schoolName: user?.schools?.[0]?.name,
      userId: user?.id,
      userName: `${user?.firstName} ${user?.lastName}`,
    });
  }, [contentNext?.moduleId, pageNumber, selectNext, trackEvent, user]);

  return (
    <>
      <div className={`${styles.content} ${className}`}>
        {children}
      </div>
      <div className={`${styles.actions} ${fullScreen ? styles.full : ''}`}>
        {leftButton ? <div className={styles.action}>{leftButton}</div> : <>
          {hasPrevContent && !hideNextButton ? <div className={styles.action}>
            <Button size={'extraSmall'} onClick={selectPrev} outline={true} label={'PREVIOUS'}
                    className={`${fullScreen ? styles.fullScreenPrevious : ''}`}/>
          </div> : <div/>}
        </>}
        {Boolean(totalPages) && Boolean(pageNumber) && <div className={styles.pages}>
        <span className={styles.currentPage}>{pageNumber}</span>/{totalPages}
        </div>}
        {rightButton ? <div className={styles.action}>{rightButton}</div> : <>
          {hasNextContent && !hideNextButton ? <div className={styles.action}>
            <Button size={'extraSmall'} onClick={onNext} label={'CONTINUE'}/>
          </div> : <div/>}
        </>}
      </div>
      {pauseModal && <PauseModal open={pauseModal} onClose={handlePause} onContinue={handleContinue}/>}
    </>
  );
}

export default Actions;
